import { useRef, useState, useCallback, useEffect, useMemo } from "react";
import { useAudioCapture } from "./useAudioCapture";
import { getAccessToken, getGuestId } from "../../../common/helpers";
import { logger } from "../../../common/logger";

/** A socket stuck in CONNECTING never errors on its own — give it a deadline. */
const CONNECT_TIMEOUT_MS = 8000;
const MAX_RECONNECT_ATTEMPTS = 5;

// Browsers can't set Authorization or X-Guest-ID headers on `new WebSocket(...)`. Append the
// credential as a query param so the server's HandshakeInterceptor can pick it up.
function withAuthQuery(url: string): string {
  const sep = url.includes("?") ? "&" : "?";
  const token = getAccessToken();
  if (token) {
    return `${url}${sep}token=${encodeURIComponent(token)}`;
  }
  const guestId = getGuestId();
  if (guestId) {
    return `${url}${sep}guestId=${encodeURIComponent(guestId)}`;
  }
  return url;
}

interface SttTranscriptDto {
  type: "interim" | "final" | "error";
  transcript: string;
  detectedLanguage?: string;
  timestamp: number;
}

interface UseWhisperSttOptions {
  wsUrl: string | any;
  onInterimTranscript: (text: string) => void;
  onFinalTranscript: (text: string, detectedLanguage?: string) => void;
  onError?: (error: string) => void;
  onStoppingListening?: () => void;
}

interface UseWhisperSttReturn {
  startListening: () => void;
  stopListening: () => void;
  cancelListening: () => void;
  isListening: boolean;
  isConnected: boolean;
  interimTranscript: string;
}

export function useWhisperStt({
  wsUrl,
  onInterimTranscript,
  onFinalTranscript,
  onError,
  onStoppingListening,
}: UseWhisperSttOptions): UseWhisperSttReturn {
  const [isListening, setIsListening] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");

  const wsRef = useRef<WebSocket | null>(null);
  const connectInFlightRef = useRef<Promise<void> | null>(null);
  const isListeningRef = useRef(false);
  const startInFlightRef = useRef<Promise<void> | null>(null);
  const sessionIdRef = useRef<string>("");
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const reconnectAttemptsRef = useRef(0);

  const onInterimRef = useRef(onInterimTranscript);
  const onFinalRef = useRef(onFinalTranscript);
  const onErrorRef = useRef(onError);
  const onStoppingRef = useRef(onStoppingListening);
  onInterimRef.current = onInterimTranscript;
  onFinalRef.current = onFinalTranscript;
  onErrorRef.current = onError;
  onStoppingRef.current = onStoppingListening;

  const audioChunksRef = useRef<Blob[]>([]);
  const pendingAudioSendRef = useRef<Promise<void>>(Promise.resolve());
  // The wire format is cumulative — every send carries the whole utterance so the backend always
  // has a container with valid headers. That makes traffic quadratic in utterance length, so
  // coalesce: while one send is in flight, newer chunks collapse into a single follow-up.
  const sendInFlightRef = useRef(false);
  const resendQueuedRef = useRef(false);

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const resetPendingAudioSend = useCallback(() => {
    pendingAudioSendRef.current = Promise.resolve();
    sendInFlightRef.current = false;
    resendQueuedRef.current = false;
  }, []);

  const enqueueAudioSend = useCallback((sendWork: () => Promise<void>) => {
    const queuedSend = pendingAudioSendRef.current
      .catch(() => undefined)
      .then(sendWork)
      .catch((error) => {
        logger.error("Failed to send Whisper audio chunk:", error);
      });

    pendingAudioSendRef.current = queuedSend;
    return queuedSend;
  }, []);

  const enqueueFullAudioSend = useCallback(() => {
    if (audioChunksRef.current.length === 0) {
      return pendingAudioSendRef.current;
    }

    const fullBlob = new Blob(audioChunksRef.current, {
      type:
        audioChunksRef.current[audioChunksRef.current.length - 1]?.type ||
        "audio/webm",
    });

    return enqueueAudioSend(async () => {
      const buffer = await fullBlob.arrayBuffer();
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(buffer);
      }
    });
  }, [enqueueAudioSend]);

  const scheduleAudioSend = useCallback(() => {
    if (sendInFlightRef.current) {
      resendQueuedRef.current = true;
      return;
    }
    sendInFlightRef.current = true;
    enqueueFullAudioSend().then(() => {
      sendInFlightRef.current = false;
      if (resendQueuedRef.current) {
        resendQueuedRef.current = false;
        scheduleAudioSend();
      }
    });
  }, [enqueueFullAudioSend]);

  const handleAudioChunk = useCallback(
    (chunk: Blob) => {
      audioChunksRef.current.push(chunk);
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        scheduleAudioSend();
      }
    },
    [scheduleAudioSend]
  );

  const stopListeningRef = useRef<(() => void) | null>(null);

  const handleSilenceDetected = useCallback(() => {
    if (isListeningRef.current) {
      logger.log("[WhisperSTT] Silence detected, stopping...");
      // Use setTimeout(0) to break out of the VAD interval's call stack
      // This ensures clean teardown of AudioContext and MediaStream
      setTimeout(() => {
        stopListeningRef.current?.();
      }, 0);
    }
  }, []);

  const {
    startRecording,
    stopRecording,
    error: captureError,
  } = useAudioCapture({
    onAudioChunk: handleAudioChunk,
    chunkIntervalMs: 1000,
    onSilence: handleSilenceDetected,
    silenceThreshold: 0.01,
    silenceDurationMs: 1200,
  });

  // useAudioCapture swallows getUserMedia failures into its own `error` state instead of throwing,
  // so startListening() resolves "successfully" with no microphone behind it. Surface that here —
  // otherwise the UI sits in a listening/processing state that can never complete. The usual cause
  // on phones is an insecure origin (http://<lan-ip>): getUserMedia is unavailable outside a
  // secure context.
  useEffect(() => {
    if (!captureError) return;
    isListeningRef.current = false;
    setIsListening(false);
    setInterimTranscript("");
    onErrorRef.current?.(captureError);
  }, [captureError]);

  const connectWebSocket = useCallback((): Promise<void> => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }
    // A socket already dialling must be awaited, not raced. Opening a second one here orphans
    // the first (its onclose then triggers a reconnect) and the backend sees two sessions.
    if (connectInFlightRef.current) {
      return connectInFlightRef.current;
    }

    if (!wsUrl) {
      return Promise.reject(new Error("Whisper STT WebSocket URL is not configured"));
    }

    const attempt = new Promise<void>((resolve, reject) => {
      let settled = false;
      let ws: WebSocket;

      try {
        ws = new WebSocket(withAuthQuery(wsUrl));
      } catch (err) {
        reject(err);
        return;
      }
      wsRef.current = ws;

      const timeoutId = setTimeout(() => {
        if (settled) return;
        settled = true;
        // Leaves the UI listening with a socket that will never carry audio otherwise.
        try {
          ws.close();
        } catch {
          /* already closing */
        }
        reject(new Error("WebSocket connection timed out"));
      }, CONNECT_TIMEOUT_MS);

      ws.onopen = () => {
        clearTimeout(timeoutId);
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;

        // Always open the backend session from here, so a reconnect mid-utterance re-arms it.
        // Without this the server's SessionState stays inactive, silently drops every audio
        // frame, and answers `stop` with an empty transcript — the mic looks live and
        // transcribes nothing.
        if (isListeningRef.current) {
          ws.send(
            JSON.stringify({ action: "start", sessionId: sessionIdRef.current })
          );
          // The cumulative payload means one resend restores the whole utterance.
          if (audioChunksRef.current.length > 0) {
            scheduleAudioSend();
          }
        }

        if (!settled) {
          settled = true;
          resolve();
        }
      };

      ws.onmessage = (event) => {
        try {
          const data: SttTranscriptDto = JSON.parse(event.data);

          if (data.type === "interim") {
            setInterimTranscript(data.transcript);
            onInterimRef.current(data.transcript);
          } else if (data.type === "final") {
            setInterimTranscript("");
            onFinalRef.current(data.transcript, data.detectedLanguage);
          } else if (data.type === "error") {
            onErrorRef.current?.(data.transcript || "Transcription error");
          }
        } catch (e) {
          logger.error("Failed to parse STT message:", e);
        }
      };

      ws.onclose = () => {
        clearTimeout(timeoutId);
        setIsConnected(false);
        if (wsRef.current === ws) {
          wsRef.current = null;
        }

        if (!settled) {
          settled = true;
          reject(new Error("WebSocket closed before opening"));
          return;
        }

        if (!isListeningRef.current) return;

        if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          logger.error("[WhisperSTT] giving up after repeated reconnect failures");
          isListeningRef.current = false;
          setIsListening(false);
          stopRecording().catch(() => undefined);
          onErrorRef.current?.("Lost connection to the transcription service");
          return;
        }

        const delay = Math.min(
          1000 * Math.pow(2, reconnectAttemptsRef.current),
          10000
        );
        reconnectAttemptsRef.current++;
        clearReconnectTimer();
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null;
          if (!isListeningRef.current) return;
          connectWebSocket().catch(() => undefined);
        }, delay);
      };

      ws.onerror = () => {
        // `error` is always followed by `close`; let onclose own the teardown so the two paths
        // can't both settle or both schedule a reconnect.
        if (settled) return;
        clearTimeout(timeoutId);
        settled = true;
        reject(new Error("WebSocket connection failed"));
      };
    });

    const tracked = attempt.finally(() => {
      if (connectInFlightRef.current === tracked) {
        connectInFlightRef.current = null;
      }
    });
    connectInFlightRef.current = tracked;
    return tracked;
  }, [wsUrl, scheduleAudioSend, stopRecording, clearReconnectTimer]);

  const startListening = useCallback(async () => {
    // Dictaphone's mode-switch and auto-start effects can both fire in the same commit; without
    // this they each open a stream and a socket, and the orphaned pair keeps the mic captured.
    if (startInFlightRef.current) return startInFlightRef.current;

    const run = async () => {
      try {
        clearReconnectTimer();
        reconnectAttemptsRef.current = 0;
        audioChunksRef.current = [];
        resetPendingAudioSend();
        sessionIdRef.current =
          globalThis.crypto?.randomUUID?.() || Date.now().toString();
        isListeningRef.current = true;
        setIsListening(true);
        setInterimTranscript("");

        // Open the mic alongside the socket rather than behind it. Serialising them means a slow
        // or unreachable backend delays — or entirely prevents — microphone capture, which is
        // indistinguishable from the mic not working.
        await Promise.all([connectWebSocket(), startRecording()]);
      } catch (err) {
        logger.error("Failed to start Whisper STT:", err);
        isListeningRef.current = false;
        setIsListening(false);
        setInterimTranscript("");
        clearReconnectTimer();
        await stopRecording().catch(() => undefined);
        onErrorRef.current?.("Failed to start speech recognition");
      }
    };

    const pending = run().finally(() => {
      startInFlightRef.current = null;
    });
    startInFlightRef.current = pending;
    return pending;
  }, [
    connectWebSocket,
    resetPendingAudioSend,
    startRecording,
    stopRecording,
    clearReconnectTimer,
  ]);

  const stopListening = useCallback(async () => {
    onStoppingRef.current?.();
    isListeningRef.current = false;
    setIsListening(false);
    clearReconnectTimer();
    await stopRecording();
    // A stop issued while the socket is still dialling must wait for it, or the readiness check
    // below would report a lost connection for a merely slow one.
    if (connectInFlightRef.current) {
      await connectInFlightRef.current.catch(() => undefined);
    }
    await enqueueFullAudioSend();

    // No socket means no `final` is ever coming, and the caller would sit in "Processing…" until
    // its timeout. Reset now instead.
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      setInterimTranscript("");
      onErrorRef.current?.("Lost connection to the transcription service");
      return;
    }

    // Enqueue "stop" through the same send queue — guarantees it arrives
    // after the last audio chunk, but stopListening returns immediately.
    enqueueAudioSend(async () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ action: "stop" }));
      }
    });
  }, [stopRecording, enqueueAudioSend, enqueueFullAudioSend, clearReconnectTimer]);

  stopListeningRef.current = stopListening;

  const cancelListening = useCallback(() => {
    isListeningRef.current = false;
    setIsListening(false);
    setInterimTranscript("");
    clearReconnectTimer();
    audioChunksRef.current = [];
    resetPendingAudioSend();
    stopRecording().catch(() => undefined);

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action: "cancel" }));
    }
  }, [resetPendingAudioSend, stopRecording, clearReconnectTimer]);

  useEffect(() => {
    return () => {
      isListeningRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      connectInFlightRef.current = null;
    };
  }, []);

  // Memoize the return so consumers get a STABLE object reference across renders. The functions
  // are already useCallback-stable, so this identity changes only when real STT state changes
  // (isListening / isConnected / interimTranscript). Without this, every render produced a new
  // object, re-running every effect that depends on `whisperStt` — including an infinite
  // stopListening()/setState loop in Dictaphone's disabled-cleanup effect.
  return useMemo(
    () => ({
      startListening,
      stopListening,
      cancelListening,
      isListening,
      isConnected,
      interimTranscript,
    }),
    [
      startListening,
      stopListening,
      cancelListening,
      isListening,
      isConnected,
      interimTranscript,
    ]
  );
}
